import React from 'react';
import { DateTime } from 'luxon';
import { getReceiptData } from '../../utils/checks';

function displayName(item, data) {
  if (item.displayName === 'Stork Club Purchase') {
    const legacy = data.productDescription.split('Manual Charge: ')[1];

    if (legacy) return `${item.displayName} - ${legacy}`;

    return `${item.displayName} - ${data.productDescription}`;
  }

  return item.displayName;
}

const Receipt = ({ data }) => {
  const { restaurantDetails, order, customer } = data ?? {};

  const check = order?.checks?.[0];

  const {
    total,
    subtotal,
    preDiscountSubtotal,
    tipAmount,
    appliedDiscounts,
    appliedServiceCharges,
    consolidatedItems,
  } = getReceiptData(check);

  const formatDate = (dateString) => {
    try {
      return DateTime.fromISO(dateString).toFormat('MM/dd/yyyy, HH:mm');
    } catch {
      return dateString;
    }
  };

  const getPaymentMethodName = (payment) => {
    if (payment.type === 'CREDIT') {
      return `${payment.cardType || 'Card'}${
        payment.last4Digits ? ` (${payment.last4Digits})` : ''
      }`;
    } else if (payment.type === 'OTHER' && payment.otherPayment) {
      return 'StorkPay';
    }
    return payment.type;
  };

  const truncateGuid = (guid) => {
    if (!guid) return '';
    return `${guid.substring(0, 6)}..${guid.substring(guid.length - 3)}`;
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.length !== 10) return phoneNumber;
    return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(
      3,
      6,
    )}-${phoneNumber.substring(6)}`;
  };

  if (!check) {
    return <div>No receipt data available</div>;
  }

  return (
    <div className="font-sans text-[9px]">
      {/* Restaurant Information */}
      <div className="mb-3 text-center">
        <h1 className="mb-0.5 text-base font-bold">
          {restaurantDetails?.general?.name || 'Stork Club'}
        </h1>

        {!restaurantDetails?.location?.address1 &&
        !restaurantDetails?.location?.address2 &&
        !restaurantDetails?.location?.city &&
        !restaurantDetails?.location?.stateCode &&
        !restaurantDetails?.location?.zipCode ? null : (
          <p className="leading-tight text-[#718096]">
            {restaurantDetails?.location?.address1 || ''}{' '}
            {restaurantDetails?.location?.address2 || ''},{' '}
            {restaurantDetails?.location?.city || ''}{' '}
            {restaurantDetails?.location?.stateCode || ''}{' '}
            {restaurantDetails?.location?.zipCode || ''} •{' '}
            {formatPhoneNumber(restaurantDetails?.location?.phone || '')}
          </p>
        )}
      </div>

      {/* Order & Customer Information - Combined */}
      <div className="mb-2">
        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 leading-tight">
          <span className="font-semibold">Order ID:</span>
          <span>{truncateGuid(order.guid)}</span>

          <span className="font-semibold">Check ID:</span>
          <span>{truncateGuid(check.guid)}</span>

          <span className="font-semibold">Date:</span>
          <span>{formatDate(check.paidDate)}</span>

          <span className="font-semibold">Customer:</span>
          <span>
            {customer ? `${customer.firstName} ${customer.lastName}` : 'Guest'}
          </span>

          <span className="font-semibold">Source:</span>
          <span>{order.source || 'N/A'}</span>
        </div>
      </div>

      {/* Itemized Purchase */}
      <div className="mb-2">
        <div className="mb-0.5 text-[10px] font-semibold">Items</div>
        <div className="mb-2 w-full">
          {/* Table Header */}
          <div className="mb-0.5 flex w-full pb-0.5">
            <div className="flex-1 text-left font-semibold">Item</div>
            <div className="w-8 text-center font-semibold">Qty</div>
            <div className="w-16 text-right font-semibold">Price</div>
            <div className="w-16 text-right font-semibold">Total</div>
          </div>

          {/* Table Body */}
          {consolidatedItems.map((item, index) => (
            <div key={index} className="flex w-full py-0.5">
              <div className="flex-1 text-left">
                {displayName(item, data)}
              </div>
              <div className="w-8 text-center">{item.quantity}</div>
              <div className="w-16 text-right">
                ${(item.preDiscountPrice ?? item.price ?? 0).toFixed(2)}
              </div>
              <div className="w-16 text-right">
                $
                {(
                  (item.preDiscountPrice ?? item.price ?? 0) * item.quantity
                ).toFixed(2)}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-1 grid grid-cols-2">
          <div></div>
          <div className="space-y-1">
            <>
              <div className="flex justify-between">
                <span>Original Subtotal:</span>
                <span>${preDiscountSubtotal.toFixed(2)}</span>
              </div>
              {appliedDiscounts &&
                appliedDiscounts.length > 0 &&
                appliedDiscounts.map((discount) => {
                  return (
                    <div
                      key={discount.name}
                      className="flex justify-between pl-2 text-[8px]"
                    >
                      <span>
                        {discount.name} ({discount.percentage}
                        %):
                      </span>
                      <span>-${discount.amount.toFixed(2)}</span>
                    </div>
                  );
                })}
            </>
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>

            {appliedServiceCharges &&
              appliedServiceCharges.length > 0 &&
              appliedServiceCharges.map((charge) => {
                return (
                  <div key={charge.name} className="flex justify-between">
                    <span>{charge.name}</span>
                    <span>${charge.amount.toFixed(2)}</span>
                  </div>
                );
              })}

            <div className="flex justify-between">
              <span>Tax:</span>
              <span>${(check.taxAmount || 0).toFixed(2)}</span>
            </div>

            <div className="flex justify-between">
              <span>Tip:</span>
              <span>${tipAmount.toFixed(2)}</span>
            </div>

            <div className="mt-1 flex justify-between pt-1 font-bold">
              <span>Total:</span>
              <span>${total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Details */}
      <div className="mb-2">
        <div className="mb-0.5 text-[10px] font-semibold">Payment Details</div>
        <div className="w-full">
          {/* Payment Items */}
          {(check.payments ?? []).map((payment, index) => (
            <div key={index} className="flex justify-between py-0.5">
              <div>
                {getPaymentMethodName(payment)}
                <span className="ml-1 text-[8px] text-[#718096]">
                  ({truncateGuid(payment.guid)})
                </span>
              </div>
              <div className="text-right">
                ${(payment.amount + (payment.tipAmount || 0)).toFixed(2)}
              </div>
            </div>
          ))}

          {/* Total Paid */}
          <div className="mt-0.5 flex justify-between pt-0.5 font-bold">
            <div>Total Paid</div>
            <div className="text-right">${total.toFixed(2)}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-2 pt-1 text-center text-[#718096]">
        <p className="mb-0.5 font-bold">Thank you for dining with us!</p>
        <p className="mt-1 text-[8px]">
          Generated: {formatDate(new Date().toISOString())}
        </p>
      </div>
    </div>
  );
};

export default Receipt;
